import * as React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { FlexCol } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { DsiInput as Input } from '../General/Input';
import { TextP } from '../../Defaults/Text';
import { Popup } from '../../Zemplin/General/Popup';
import { OnlyText } from './FormInput';

export interface Props {
  label?: string;
  marginRight?: number;
  marginBottom?: number;
  maxWidth?: number;
  required?: boolean;
  notRequired?: boolean;
  maxWidthPx?: number;
  minWidthPx?: number;
  validMessage?: string;
  inputRef?: any;
  justText?: boolean;
  id?: string;
  name?: string;
  value: string | number | undefined;
  options: Array<{
    name: string;
    value: string;
  }>;
  defaultValue?: {
    name: string;
    value: string | undefined | number;
  };
  onChange: (e: any) => void;
  onBlur?: (e?: any) => void;
  backgroundColor?: string;
  height?: number;
  autoComplete?: string;
  placeholder?: string;
  [x: string]: any;
}

function FormSelect({
  label,
  maxWidth = 100,
  marginRight = 0,
  marginBottom = 16,
  maxWidthPx = 320,
  minWidthPx,
  required,
  notRequired,
  validMessage,
  inputRef,
  justText,
  id,
  name = 'select',
  options,
  value,
  defaultValue,
  onChange,
  onBlur,
  backgroundColor,
  height = 56,
  autoComplete = 'off',
  placeholder,
  ...props
}: Props) {
  if (props.type === 'hidden') {
    return (
      <Input
        ref={inputRef}
        showError={validMessage}
        required={required}
        {...props}
      />
    );
  }

  if (justText) {
    return (
      <OnlyText>
        <b>{`${label}: `}</b>
        {`${value || ''}`}
      </OnlyText>
    );
  }

  return (
    <Wrapper
      marginBottom={marginBottom}
      marginRight={marginRight}
      maxWidth={maxWidth}
      maxWidthPx={maxWidthPx}
      minWidthPx={minWidthPx}
    >
      {label && (
        <Label>
          {label}
          {required && <Required>{' *'}</Required>}
          {notRequired && <NotRequired>{`(${__('Nepovinné')})`}</NotRequired>}
        </Label>
      )}
      <StyledSelect
        id={id ? id : ''}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete={autoComplete}
        background={backgroundColor}
        height={height}
      >
        {placeholder && (
          <option value="" disabled={true} selected={true}>
            {placeholder}
          </option>
        )}
        {defaultValue && (
          <option value={defaultValue!.value}>{defaultValue!.name}</option>
        )}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.name}
          </option>
        ))}
      </StyledSelect>
      <Popup bottom={rem(42)} right={rem(16)} message={validMessage || ''} />
    </Wrapper>
  );
}

const Wrapper = styled(FlexCol)<{
  maxWidth: number;
  maxWidthPx: number;
  marginRight: number;
  marginBottom: number;
  minWidthPx?: number;
}>`
  width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}%` : '100%;')};
  max-width: ${({ maxWidthPx }) => rem(maxWidthPx)};
  min-width: ${({ minWidthPx }) => (minWidthPx ? rem(minWidthPx) : 'initial')};
  margin-bottom: ${({ marginBottom }) => rem(marginBottom)};
  margin-right: ${({ marginRight }) => rem(marginRight)};
  position: relative;

  ${({ theme }) => theme.mediab.m580`
    &:last-child {
      margin-right: 0;
    }
    margin-right: ${({ maxWidthPx, marginRight }) =>
      maxWidthPx !== 320 && marginRight ? rem(marginRight) : 0};
  `}

  ${({ theme }) => theme.mediab.s450`
    max-width: initial;
    min-width: initial;
  `}
`;

const Label = styled(TextP)`
  font-size: ${rem(16)};
  margin-bottom: ${rem(8)};
  ${({ theme }) => theme.mediab.m810`
    font-size: ${rem(14)};
  `}
`;

const Required = styled.span`
  font-size: ${rem(16)};
  color: ${({ theme }) => theme.colors.textPrimary};
  ${({ theme }) => theme.mediab.m810`
    font-size: ${rem(14)};
  `}
`;

const NotRequired = styled.span`
  margin-left: ${rem(8)};
  color: ${({ theme }) => theme.colors.placeholder};
  font-size: ${rem(12)};
  font-family: ${({ theme }) => theme.font.tertiary};
  line-height: ${rem(20)};
`;

const StyledSelect = styled.select<{ background?: string; height: number }>`
  width: 100%;
  cursor: pointer;
  height: ${({ height }) => rem(height)};
  background: ${({ background, theme }) =>
    background ? background : theme.colors.white};
  border: ${rem(1)} solid #cccccc;
  border-radius: ${rem(4)};
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.08);
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${rem(16)};
  padding: ${rem(8)} ${rem(32)} ${rem(8)} ${rem(16)};
  appearance: none;
  box-shadow: ${({ theme }) => theme.boxShadow.input};
  font-weight: 400;
  display: flex;
  align-items: center;

  background-image: url('/images/arrow-triangle.svg');
  background-position: 95% 50%;
  background-size: 8px 4px;
  background-repeat: no-repeat;

  ${({ theme }) => theme.mediab.m810`
    font-size: ${rem(14)};
  `}

  & > option[value=''][disabled] {
    display: none;
  }

  &:invalid {
    color: ${({ theme }) => theme.colors.placeholder};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }

  ${({ theme }) => theme.mediab.m810`
    height: ${rem(40)};
  `}
`;

export { FormSelect };
